import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import LessonPlayer from './components/LessonPlayer';
import LessonInfo from './components/LessonInfo';
import LessonSidebar from './components/LessonSidebar';
import NotesPanel from './components/NotesPanel';
import DiscussionPanel from './components/DiscussionPanel';
import NavigationControls from '../activity-interface/components/NavigationControls';
import { useLesson } from '../../hooks/useCourseData';

const StudentLessonPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams?.get('id') || '1';
  const courseId = searchParams?.get('courseId') || '1';

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [activeTab, setActiveTab] = useState('outline');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [notes, setNotes] = useState([]);
  const [discussionMessages, setDiscussionMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lessonProgress, setLessonProgress] = useState(0);

  // Carregar dados reais da lição
  const { lesson: realLesson, loading: lessonLoading, error: lessonError } = useLesson(lessonId);

  // Debug: log dos dados carregados
  useEffect(() => {
    console.log('🔍 StudentLessonPage Debug:', {
      lessonId,
      realLesson,
      lessonLoading,
      lessonError,
      mockLesson: mockLesson.id
    });
  }, [lessonId, realLesson, lessonLoading, lessonError]);

  // Mock lesson data
  const mockLesson = {
    id: lessonId,
    title: "Liderança Cristã: Princípios Fundamentais",
    description: `Esta lição aborda os princípios essenciais da liderança cristã baseada nos ensinamentos bíblicos. Você aprenderá como desenvolver uma liderança servidora, inspirada no exemplo de Cristo, e como aplicar esses princípios no seu contexto ministerial.

Exploraremos as características de um líder cristão eficaz, incluindo humildade, integridade, visão espiritual e capacidade de equipar outros para o ministério. Também discutiremos os desafios contemporâneos da liderança na igreja e como superá-los com sabedoria bíblica.`,
    duration: 2100, // 35 minutes
    moduleNumber: 1,
    lessonNumber: 2,
    publishedAt: "2025-01-10T10:00:00Z",
    views: 856,
    estimatedTime: "35 min",
    lessonType: "video", // video, text, interactive
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
    objectives: [
      "Compreender os fundamentos bíblicos da liderança cristã",
      "Identificar características essenciais de um líder servo",
      "Aplicar princípios de liderança no contexto ministerial",
      "Desenvolver estratégias para equipar novos líderes"
    ],
    tags: ["Liderança", "Ministério", "Desenvolvimento Pessoal", "Teologia Prática"],
    instructor: {
      name: "Pastor João Carlos Silva",
      title: "Doutor em Ministério Pastoral",
      bio: "Pastor há mais de 20 anos, com doutorado em Ministério Pastoral e especialização em Liderança Cristã.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    course: {
      id: courseId,
      title: "Fundamentos de Liderança Cristã",
      totalLessons: 12,
      currentLessonIndex: 2
    }
  };

  // Mock course outline
  const mockCourseOutline = [
    {
      id: 1,
      title: "Introdução à Liderança Cristã",
      duration: 1800,
      completed: true,
      isLocked: false
    },
    {
      id: 2,
      title: "Liderança Cristã: Princípios Fundamentais",
      duration: 2100,
      completed: false,
      isLocked: false,
      isCurrent: true
    },
    {
      id: 3,
      title: "O Líder Como Servo",
      duration: 1950,
      completed: false,
      isLocked: false
    },
    {
      id: 4,
      title: "Comunicação Eficaz na Liderança",
      duration: 2250,
      completed: false,
      isLocked: false
    },
    {
      id: 5,
      title: "Desenvolvendo Outros Líderes",
      duration: 2400,
      completed: false,
      isLocked: true
    }
  ];

  // Mock notes
  const mockNotes = [
    {
      id: 1,
      text: "A liderança cristã deve sempre começar com o exemplo pessoal",
      timestamp: 340,
      createdAt: "2025-01-13T10:30:00Z"
    },
    {
      id: 2,
      text: "Servir primeiro, liderar depois - princípio fundamental do Reino",
      timestamp: 720,
      createdAt: "2025-01-13T10:35:00Z"
    }
  ];

  // Mock discussion messages
  const mockDiscussion = [
    {
      id: 1,
      user: "Maria Santos",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face",
      message: "Excelente lição! Como aplicar esses princípios em um ministério jovem?",
      timestamp: "2025-01-13T11:00:00Z",
      replies: [
        {
          id: 2,
          user: "Pastor João Carlos",
          avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face",
          message: "Ótima pergunta, Maria! Os jovens respondem muito bem ao exemplo autêntico. Na próxima lição vamos falar especificamente sobre liderança geracional.",
          timestamp: "2025-01-13T11:15:00Z",
          isInstructor: true
        }
      ]
    },
    {
      id: 3,
      user: "Carlos Lima",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
      message: "A parte sobre humildade na liderança foi transformadora para mim.",
      timestamp: "2025-01-13T11:30:00Z",
      replies: []
    }
  ];

  useEffect(() => {
    // Atualizar loading baseado no carregamento da lição real
    setIsLoading(lessonLoading);
    
    // Inicializar dados mock apenas se não estiver carregando
    if (!lessonLoading) {
      setNotes(mockNotes);
      setDiscussionMessages(mockDiscussion);
    }
  }, [lessonId, lessonLoading]);

  const handleTimeUpdate = (current, total) => {
    setCurrentTime(current);
    setDuration(total);
    
    // Update lesson progress
    const progress = total > 0 ? (current / total) * 100 : 0;
    setLessonProgress(progress);
  };

  const handleLessonComplete = () => {
    console.log('Lesson completed');
    // Mark lesson as completed and navigate to next
  };

  const handleToggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleShareLesson = () => {
    if (navigator.share) {
      navigator.share({
        title: mockLesson?.title,
        text: mockLesson?.description,
        url: window.location?.href
      });
    } else {
      navigator.clipboard?.writeText(window.location?.href);
    }
  };

  const handleLessonNavigation = (lessonId) => {
    navigate(`/student-lesson-page?id=${lessonId}&courseId=${courseId}`);
  };

  const handleAddNote = (note) => {
    const newNote = {
      id: Date.now(),
      text: note?.text,
      timestamp: note?.timestamp || currentTime,
      createdAt: new Date()?.toISOString()
    };
    setNotes(prev => [...prev, newNote]);
  };

  const handleDeleteNote = (noteId) => {
    setNotes(prev => prev?.filter(note => note?.id !== noteId));
  };

  const handleEditNote = (noteId, newText) => {
    setNotes(prev => prev?.map(note => 
      note?.id === noteId ? { ...note, text: newText } : note
    ));
  };

  const handleSeekToNote = (timestamp) => {
    setCurrentTime(timestamp);
  };

  const handleAddDiscussionMessage = (message) => {
    const newMessage = {
      id: Date.now(),
      user: "João Silva", // Current user
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
      message: message,
      timestamp: new Date()?.toISOString(),
      replies: []
    };
    setDiscussionMessages(prev => [...prev, newMessage]);
  };

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Cursos', path: '/courses' },
    { label: realLesson?.course?.title || mockLesson?.course?.title || 'Curso', path: `/course-detail-view?id=${courseId}` },
    { label: realLesson?.title || mockLesson?.title || 'Lição', path: `/student-lesson-page?id=${lessonId}&courseId=${courseId}`, isActive: true }
  ];

  const tabs = [
    { id: 'outline', label: 'Estrutura do Curso', icon: 'List' },
    { id: 'notes', label: 'Minhas Anotações', icon: 'FileText' },
    { id: 'discussion', label: 'Discussão', icon: 'MessageSquare' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Carregando lição... - Academy Platform</title>
        </Helmet>
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Breadcrumb Skeleton */}
            <div className="h-6 bg-muted rounded w-96 mb-8 animate-pulse"></div>
            
            {/* Main Content Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                {/* Player Skeleton */}
                <div className="aspect-video bg-muted rounded-lg animate-pulse"></div>
                {/* Info Skeleton */}
                <div className="space-y-4">
                  <div className="h-8 bg-muted rounded w-3/4 animate-pulse"></div>
                  <div className="h-4 bg-muted rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-muted rounded w-2/3 animate-pulse"></div>
                </div>
              </div>
              {/* Sidebar Skeleton */}
              <div className="space-y-4">
                <div className="h-80 bg-muted rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{mockLesson?.title} - Academy Platform</title>
        <meta name="description" content={mockLesson?.description?.substring(0, 155)} />
      </Helmet>

      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Back Navigation & Breadcrumb */}
          <div className="flex items-center justify-between mb-8 min-w-0">
            <div className="flex items-center space-x-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                onClick={() => navigate(`/course-detail-view?id=${courseId}`)}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={20}
              >
                Voltar ao curso
              </Button>
              <div className="min-w-0 flex-1">
                <Breadcrumb customItems={breadcrumbItems} />
              </div>
            </div>
            
            {/* Progress Indicator */}
            <div className="hidden md:flex items-center space-x-4 bg-card rounded-lg px-4 py-2 shadow-tier-1">
              <span className="text-sm text-muted-foreground">
                Lição {mockLesson?.lessonNumber} de {mockLesson?.course?.totalLessons}
              </span>
              <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-state"
                  style={{ width: `${lessonProgress}%` }}
                />
              </div>
              <span className="text-sm font-medium text-foreground">
                {Math.round(lessonProgress)}%
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Lesson Player */}
              <LessonPlayer
                lesson={realLesson || mockLesson}
                onTimeUpdate={handleTimeUpdate}
                onComplete={handleLessonComplete}
                currentTime={currentTime}
                duration={duration}
              />

              {/* Lesson Information */}
              <LessonInfo
                lesson={realLesson || mockLesson}
                progress={lessonProgress}
                isBookmarked={isBookmarked}
                onToggleBookmark={handleToggleBookmark}
                onShare={handleShareLesson}
              />

              {/* Mobile Tabs */}
              <div className="lg:hidden">
                <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-6">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-micro ${
                        activeTab === tab?.id
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>

                {/* Mobile Tab Content */}
                {activeTab === 'outline' && (
                  <LessonSidebar
                    courseOutline={mockCourseOutline}
                    currentLessonId={parseInt(lessonId)}
                    onLessonSelect={handleLessonNavigation}
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  />
                )}

                {activeTab === 'notes' && (
                  <NotesPanel
                    notes={notes}
                    currentTime={currentTime}
                    onAddNote={handleAddNote}
                    onDeleteNote={handleDeleteNote}
                    onEditNote={handleEditNote}
                    onSeekToNote={handleSeekToNote}
                  />
                )}

                {activeTab === 'discussion' && (
                  <DiscussionPanel
                    messages={discussionMessages}
                    onAddMessage={handleAddDiscussionMessage}
                  />
                )}
              </div>

              {/* Navigation Controls */}
              <NavigationControls
                currentLesson={mockLesson}
                courseOutline={mockCourseOutline}
                onNavigate={handleLessonNavigation}
                currentQuestion={1}
                totalQuestions={1}
                canGoNext={true}
                canGoPrevious={true}
                onPrevious={() => {}}
                onNext={() => {}}
                onSkip={() => {}}
                onSubmit={() => {}}
                isLastQuestion={false}
                hasUnsavedChanges={false}
              />
            </div>

            {/* Desktop Sidebar */}
            <div className="hidden lg:block space-y-6">
              {/* Course Outline */}
              <LessonSidebar
                courseOutline={mockCourseOutline}
                currentLessonId={parseInt(lessonId)}
                onLessonSelect={handleLessonNavigation}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              />

              {/* Notes Panel */}
              <NotesPanel
                notes={notes}
                currentTime={currentTime}
                onAddNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
                onEditNote={handleEditNote}
                onSeekToNote={handleSeekToNote}
              />

              {/* Discussion Panel */}
              <DiscussionPanel
                messages={discussionMessages}
                onAddMessage={handleAddDiscussionMessage}
              />
            </div>
          </div>
        </div>
      </main>

      <FloatingAIChat />
    </div>
  );
};

export default StudentLessonPage;